import React, { CSSProperties, ReactNode } from 'react'

import { ReactComponent as DotsThreeIcon } from '@app/assets/Small/Outline/DotsThree.svg'
import { useIntl } from '@app/intl/Intl'
import { IconButton } from '@app/lib/inputs/IconButton/IconButton'
import { useMobile } from '@app/services/useMobile'

import { DropdownMenuDesktop } from './DropdownMenuDesktop'
import { DropdownMenuMobile } from './DropdownMenuMobile'
import { MenuItemContent } from './MenuItemContent'
import { DropdownItem, DropdownMenuAlign, DropdownMenuSize } from './types'

import './DropdownMenu.scss'

export type DropdownMenuProps = {
  items: DropdownItem[]
  trigger?: ReactNode
  size?: DropdownMenuSize
  align?: DropdownMenuAlign
  width?: CSSProperties['width']
  matchTriggerWidth?: boolean
  modal?: boolean
  className?: string
}

const DropdownMenuRoot = ({
  items,
  trigger,
  size = 'md',
  align = 'right',
  width,
  matchTriggerWidth,
  modal,
  className
}: DropdownMenuProps) => {
  const { isMobile } = useMobile()
  const { formatMessage } = useIntl()

  if (!items.length) {
    return null
  }

  const triggerNode = trigger ?? (
    <IconButton
      color='secondary'
      variant='outlined'
      aria-label={formatMessage({ id: 'Lib.DropdownMenu.Trigger' })}
    >
      <DotsThreeIcon />
    </IconButton>
  )

  if (isMobile) {
    return (
      <DropdownMenuMobile
        items={items}
        trigger={triggerNode}
        title={formatMessage({ id: 'Lib.DropdownMenu.Trigger' })}
        size={size}
        className={className}
      />
    )
  }

  return (
    <DropdownMenuDesktop
      items={items}
      trigger={triggerNode}
      size={size}
      align={align}
      width={width}
      matchTriggerWidth={matchTriggerWidth}
      modal={modal}
      className={className}
    />
  )
}

export const DropdownMenu = Object.assign(DropdownMenuRoot, { Item: MenuItemContent })
